import { Injectable } from '@angular/core';
import { FirebaseService } from './firebase.service';
import { Storage } from '@ionic/storage';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ModuleService {
  private readonly baseUrl: string;

  constructor(
    private httpClient: HttpClient,
    private firebaseService: FirebaseService,
    private storage: Storage
  ) {
    this.baseUrl = `${environment.apiUrl}/modules`;
  }

  public getCompanyModules = async (): Promise<any[]> => {
    const user = await this.storage.get('user');
    const modules: string[] = await this.firebaseService.getDocument(`companies/${user.companyId}/settings/availableModules`);
    if (modules.length > 0) {
      const modulesReq = [];
      for (const module of modules) {
        modulesReq.push(this.getCompanyModule(module));
      }
      return await Promise.all(modulesReq);
    }
    const modulesData = await lastValueFrom(this.getModules());
    const settingsModules = await this.firebaseService.getDocumentsListOnce(`settings/modules`);
    const result: any[] = [];
    for (const sm of settingsModules) {
      const md = modulesData.find((m: any) => m.key === sm.key);
      result.push({
        ...sm,
        title: md?.title,
        description: md?.description
      });
    }
    return result;

  };

  public getCompanyModule = async (key: string): Promise<any> => {
    const user = await this.storage.get('user');
    const [userModule, module] = await Promise.all([
      this.firebaseService.getDocument(`companies/${user.companyId}/settings/modules/${key}`),
      lastValueFrom(this.getModuleByKey(key))
    ]);
    return {
      ...(module || {}),
      ...userModule,
      title: module?.title,
      description: module?.description,
    };
  };

  public geModulePercentages = async (key: string): Promise<any> => {
    const user = await this.storage.get('user');
    const [userModule, module] = await Promise.all([
      this.firebaseService.getDocument(`companies/${user.companyId}/settings/modules/${key}/percentages`),
      await lastValueFrom(
        this.getModuleByKey(key).pipe(
          map(m => {
              const result: Record<string, number> = {};
              // eslint-disable-next-line guard-for-in
              for (const section in m?.sections) {
                result[section] = m.sections[section]?.percentage ?? 0;
              }
              return result;
            }
          )
        )
      )
    ]);
    return {
      ...(module || {}),
      ...userModule
    };
  };

  public getUserModules = async (): Promise<any[]> => {
    const user = await this.storage.get('user');
    return await this.firebaseService.getDocumentsListOnce(`user_journey/${user.companyLowercase}/${user.uid}/modules`);
  };

  public getUserModule = async (moduleKey: string): Promise<any> => {
    const user = await this.storage.get('user');
    return await this.firebaseService.getDocumentsListOnce(`user_journey/${user.companyLowercase}/modules/${moduleKey}`);
  };

  public getStatusModuleSections = async (module: string): Promise<any> => {
    const [avatar, bestPractices, introduction, peer] = await Promise.all([
      this.getStatusModuleSection(module, 'avatar'),
      this.getStatusModuleSection(module, 'bestPractices'),
      this.getStatusModuleSection(module, 'introduction'),
      this.getStatusModuleSection(module, 'peer'),
    ]);
    return {
      avatar,
      bestPractices,
      introduction,
      peer
    };
  };

  public getStatusModuleSection = async (module: string, section: string): Promise<boolean> => {
    const user = await this.storage.get('user');
    let result: boolean = await this.firebaseService.getDocument(`companies/${user.companyId}/settings/modules/${module}/${section}`);
    if (result === null) {
      const moduleData = await lastValueFrom(this.getModuleByKey(module));
      result = moduleData?.sections[section]?.active;
    }
    return result || false;
  };

  public getModuleProperty = async (module: string, property: string): Promise<boolean> => {
    const user = await this.storage.get('user');
    let result: boolean = await this.firebaseService.getDocument(`companies/${user.companyId}/settings/modules/${module}/${property}`);
    if (result === null) {
      const moduleData = await lastValueFrom(this.getModuleByKey(module));
      result = moduleData[property];
    }
    return result || false;
  };

  public getAssessmentIdByProperty = async (module: string, assessmentId: string, assessmentIdProperty: string): Promise<string> => {
    const { data: settingsAssessmentId } = await lastValueFrom(
      this.getModulePropertyByKey(module, assessmentIdProperty)
    );
    return settingsAssessmentId || `${module}/${assessmentId}`;
  };


  public getModuleByKey = (key: string): Observable<any> => this.httpClient.get<any>(`${this.baseUrl}/key/${key}`);
  public getModulePropertyByKey = (key: string, property: string): Observable<any> =>
    this.httpClient.get<any>(`${this.baseUrl}/key/${key}/property`, { params: { property } });
  public getModules = (): Observable<any> => this.httpClient.get<any>(`${this.baseUrl}`);

}
